import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import T from '../../styles/theme';
import { Badge } from '../UI';
import { useStore } from '../../data/store';
import { useToast } from '../Toast';
import ContactForm from './ContactForm';

const EMPTY_VALUES = {
  fn: "", ln: "", email: "", ph: "", co: "", title: "",
  owner: "", source: "Inbound", type: "SMB", industry: "",
  city: "", state: "", leadStage: "new", status: "active",
  score: 50, sent: "neutral", ai: "",
  tags: [], properties: {}, activities: [],
  created: "", lastActivity: "",
};

export default function AddContactModal({
  isOpen,
  onClose,
  prefillCompany,
  prefillDealId,
  prefillOwnerId,
  onCreated,
}) {
  const store = useStore();
  const toast = useToast();
  const [values, setValues] = useState(EMPTY_VALUES);
  const [saving, setSaving] = useState(false);
  const [addingTag, setAddingTag] = useState(false);
  const [newTagValue, setNewTagValue] = useState("");

  // Seed form whenever the modal opens
  useEffect(() => {
    if (!isOpen) return;
    setValues({
      ...EMPTY_VALUES,
      co: prefillCompany || "",
      owner: prefillOwnerId || store.currentUserMember?._dbId || "",
    });
    setSaving(false);
    setAddingTag(false);
    setNewTagValue("");
  }, [isOpen, prefillCompany, prefillOwnerId, store.currentUserMember]);

  // Escape closes the modal (unless saving)
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === "Escape" && !saving) onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, saving, onClose]);

  const canSave = useMemo(
    () => (values.fn || "").trim().length > 0 && (values.ln || "").trim().length > 0 && !saving,
    [values.fn, values.ln, saving]
  );

  if (!isOpen) return null;

  const handleChange = (partial) => setValues(v => ({ ...v, ...partial }));

  const addTag = () => {
    const t = (newTagValue || "").trim();
    if (!t) { setAddingTag(false); return; }
    const existing = values.tags || [];
    if (existing.includes(t)) { toast?.info?.("Tag already exists"); setNewTagValue(""); return; }
    setValues(v => ({ ...v, tags: [...(v.tags || []), t] }));
    setNewTagValue("");
    setAddingTag(false);
  };

  const removeTag = (tag) => setValues(v => ({ ...v, tags: (v.tags || []).filter(t => t !== tag) }));

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      // Resolve company_id via the same case-insensitive find-or-create the
      // bulk import flows use. Empty/whitespace company stays null.
      const companyId = await store.findOrCreateCompany(values.co);
      const payload = {
        ...values,
        fn: (values.fn || "").trim(),
        ln: (values.ln || "").trim(),
        company: values.co,
        company_id: companyId,
      };
      const created = await store.createContact(payload);
      if (!created || !created.id || String(created.id).startsWith("contact-")) {
        toast?.error?.("Failed to create contact — please try again");
        setSaving(false);
        return;
      }
      toast?.success?.("Contact created");

      if (prefillDealId && typeof onCreated === "function") {
        onCreated(created);
      } else if (typeof onCreated === "function") {
        onCreated(created);
      }
      setSaving(false);
      onClose?.();
    } catch (err) {
      toast?.error?.("Failed to create contact: " + (err?.message || "unknown error"));
      setSaving(false);
    }
  };

  const tags = values.tags || [];

  return ReactDOM.createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div
        onClick={() => { if (!saving) onClose?.(); }}
        style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.50)", backdropFilter: "blur(4px)" }}
      />
      <div style={{
        position: "relative", width: 520, maxHeight: "85vh",
        background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)",
        borderRadius: 8, boxShadow: "0 40px 100px rgba(0,0,0,0.40)",
        display: "flex", flexDirection: "column", overflow: "hidden",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", borderBottom: "1px solid rgba(217,219,221,0.08)" }}>
          <span style={{ fontSize: 14, fontWeight: 400, color: T.txt }}>
            {prefillCompany ? "New Contact — " + prefillCompany : "New Contact"}
          </span>
          <button
            onClick={() => { if (!saving) onClose?.(); }}
            style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(217,219,221,0.12)", cursor: saving ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: T.txt }}
            aria-label="Close"
          >×</button>
        </div>

        <div style={{ flex: 1, overflow: "auto", padding: "14px 18px" }}>
          <ContactForm
            values={values}
            onChange={handleChange}
            teamMembers={store.teamMembers}
            getOwnerName={store.getOwnerName}
            customFields={store.customFields?.contacts}
            store={store}
          />

          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 9, fontWeight: 400, color: T.tx3, letterSpacing: ".06em", marginBottom: 6 }}>TAGS</div>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
              {tags.map(tag => (
                <span key={tag} onClick={() => removeTag(tag)} title="Remove" style={{ cursor: "pointer" }}>
                  <Badge color={"#3D8B8B"}>{tag} ×</Badge>
                </span>
              ))}
              {addingTag ? (
                <input
                  autoFocus
                  value={newTagValue}
                  onChange={e => setNewTagValue(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter") { e.preventDefault(); addTag(); }
                    else if (e.key === "Escape") { setAddingTag(false); setNewTagValue(""); }
                  }}
                  onBlur={() => { if (!newTagValue.trim()) setAddingTag(false); }}
                  placeholder="tag name"
                  style={{ padding: "2px 6px", borderRadius: 3, border: "1px solid " + T.bdr, background: "rgba(255,255,255,0.04)", color: T.txt, fontSize: 10, outline: "none", fontFamily: "inherit", width: 90 }}
                />
              ) : (
                <button
                  onClick={() => setAddingTag(true)}
                  style={{ padding: "2px 8px", borderRadius: 3, border: "1px dashed " + T.bdr, background: "transparent", color: T.txt, fontSize: 10, cursor: "pointer" }}
                >+ Add</button>
              )}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "12px 18px", borderTop: "1px solid rgba(217,219,221,0.08)" }}>
          <button
            onClick={() => { if (!saving) onClose?.(); }}
            disabled={saving}
            style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: T.txt, fontSize: 12, fontWeight: 400, cursor: saving ? "not-allowed" : "pointer", fontFamily: "inherit" }}
          >Cancel</button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: canSave ? T.blue : "rgba(64,89,173,0.4)", color: "#FAFAFA", fontSize: 12, fontWeight: 400, cursor: canSave ? "pointer" : "not-allowed", fontFamily: "inherit" }}
          >{saving ? "Saving…" : "Save Contact"}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}
